/**
 * **`legacy/frontend/src/screens/GatheringForm.tsx` whole, not a fork of it.**
 *
 * Everything below the imports is the old file from its line 4, in its own order: the
 * `Plan` the form is filled with, the placeholder `tomorrowEvening()` that «Когда»
 * overwrites as soon as it has an offer, `blankPlan()`, and the one component that is both
 * modes of the form.
 *
 * **Two lines differ from the old file, and both of them are the same import.**
 * `SessionKind` comes from `@contracts/index` rather than from `../api`, because a server
 * shape is defined in the frozen contract package and nowhere else — ADR-0011, the same
 * split `format.ts`, `BirthdayCard.tsx` and `WhenField.tsx` carry at their own first
 * import — and that forces the blank line the type import stands on. Nothing else in this
 * file is anything but the old app's.
 *
 * **Three lines below look like slips and are not**, and they are noted here rather than
 * beside themselves so that the ported region stays byte-identical to its source:
 *
 *   * `disabled={busy || plan.start === ''}` (`:112`) tests a value the app cannot hold:
 *     `blankPlan()` fills `start` with `tomorrowEvening()` and the edit form fills it from
 *     the gathering's own instant, so neither caller can produce `''` and «Когда» never
 *     reports one. `FND-072` is `KEEP` and AC-64 says so itself — the criterion is checked
 *     by review against this line, not by a test, because no rendered state tells the
 *     expression from `disabled={busy}` and a case that forced an empty `start` would be
 *     new machinery around a value that does not exist.
 *   * the title `<input>` (`:156-159`) carries no `placeholder` and no `type`. The
 *     placeholder is argued at `:152-155`. The old one has no `maxLength` either (`FND-069`);
 *     since C-028 (`FND-1626`) this one carries the server's 200, so a long title stops at
 *     the field instead of earning a bare «Ошибка 422».
 *   * the effect at `:87-92` excludes `initial` from its dependencies on purpose, and its
 *     `eslint-disable-next-line` is ported as the record even though this tree runs
 *     oxlint. A `useRef` work-around would change when the effect runs, which build spec
 *     §1.1 forbids by name.
 *
 * `FND-074` is `KEEP` and lands here as well as in `WhenField.tsx`: reopening the sheet
 * inside its 220 ms closing run resets the plan from `initial` while the wheel underneath
 * keeps the `opened` and the `floor` of the last opening, because the mount survived. No
 * `key` forces a remount — that is the fix the finding declined.
 */

import { useEffect, useState } from 'react';

import type { SessionKind } from '@contracts/index';

import { toLocalInputValue, words } from '../format';
import Sheet from './Sheet';
import WhenField, { type Held } from './WhenField';

/** What the form is filled with, and what it hands back. */
export type Plan = {
  kind: SessionKind;
  /** The local `YYYY-MM-DDTHH:mm` the fields carry, not an ISO instant. */
  start: string;
  /** Empty means «no title», which is a legitimate answer and not a blank. */
  title: string;
};

/**
 * Tomorrow at 19:00 — where a new gathering stands before it knows anything.
 *
 * A placeholder and not a suggestion: `WhenField` replaces it with a date the
 * choir actually keeps as soon as the history is in, and where there is no
 * history it is the wheel's resting place rather than a claim about anything.
 */
function tomorrowEvening(): string {
  const when = new Date();
  when.setDate(when.getDate() + 1);
  when.setHours(19, 0, 0, 0);
  return toLocalInputValue(when);
}

/** The plan a gathering that does not exist yet starts from. */
export function blankPlan(): Plan {
  /* Rehearsals are the many, so the form opens on one. */
  return { kind: 'rehearsal', start: tomorrowEvening(), title: '' };
}

/**
 * The one form that plans a gathering, and the one that changes the plan.
 *
 * It lived inside `ScheduleScreen` and served only the creating half. Editing
 * a scheduled gathering — which is task 2 of the critics' queue — needs the
 * same three fields prefilled from the same evening, and a second copy of a
 * form is a second set of rules about what a title may be and which kind is
 * default. Worse, it would be a second sheet: the app has three containers and
 * the rule of 2026-08-21 is that what is filled in arrives as a sheet, so two
 * sheets asking the same question is exactly the drift that rule stops.
 *
 * So it is one component with two modes, and everything that differs between
 * them is a word: what the sheet is called, what the button says, and whether
 * the kind can still be changed.
 */
export default function GatheringForm({
  open,
  mode,
  initial,
  held,
  busy,
  /**
   * Why the kind cannot be changed, or null while it can. Drawn rather than
   * hidden: a segment that silently stops answering is the disabled-versus-
   * unchosen confusion `DESIGN.md` spent a bug on.
   */
  kindLocked,
  failure,
  onSave,
  onClose,
}: {
  open: boolean;
  mode: 'new' | 'edit';
  initial: Plan;
  held: Held[];
  busy: boolean;
  kindLocked: string | null;
  failure: string | null;
  onSave: (plan: Plan) => void;
  onClose: () => void;
}) {
  const [plan, setPlan] = useState(initial);

  /**
   * The fields are filled from the caller every time the sheet is opened, and
   * not while it stands open. Following `initial` continuously would rewrite a
   * date under the regent's finger the moment the list behind reloaded; not
   * following it at all would open the edit form on whatever was typed into it
   * last time.
   */
  useEffect(() => {
    if (open) setPlan(initial);
    // `initial` is rebuilt on every render of the caller, so it cannot be a
    // dependency: it would reset the form on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const said = words(plan.kind);
  const editing = mode === 'edit';

  return (
    <Sheet
      open={open}
      label={editing ? `Изменить ${said.accusative}` : 'Новая встреча'}
      onClose={onClose}
      foot={
        <>
          {/* The last line of the sheet, and there is no «Отмена» beside it: the
              handle, the darkening and the way back all say the same thing. The
              refusal stands directly over it - the sheet does not close until
              the request has gone through, so «На это время уже есть спевка» is
              read where the tap was made. */}
          {failure !== null && <p className="failure">{failure}</p>}
          <button
            className="btn btn--primary"
            disabled={busy || plan.start === ''}
            onClick={() => onSave(plan)}
          >
            Сохранить
          </button>
        </>
      }
    >
      {/* First, because the rest of the form is worded from it — and because
          the two are genuinely different events rather than a setting on one.
          A segmented row, which is what a choice of two or three looks like
          everywhere else in this app. */}
      <div className="field">
        <span>Что заводим</span>
        <div className="seg">
          <button
            className={plan.kind === 'rehearsal' ? 'btn btn--selected' : 'btn btn--outline'}
            disabled={kindLocked !== null}
            onClick={() => setPlan({ ...plan, kind: 'rehearsal' })}
          >
            Спевка
          </button>
          <button
            className={plan.kind === 'service' ? 'btn btn--selected' : 'btn btn--outline'}
            disabled={kindLocked !== null}
            onClick={() => setPlan({ ...plan, kind: 'service' })}
          >
            Участие
          </button>
        </div>
        {kindLocked !== null && <p className="hint">{kindLocked}</p>}
      </div>
      <WhenField
        kind={plan.kind}
        held={held}
        value={plan.start}
        onChange={(start) => setPlan({ ...plan, start })}
      />
      <label className="field">
        <span>Название, если нужно</span>
        {/* No placeholder. An example of a title is an example of what to call
            a gathering, and this app has no business suggesting that — least
            of all for a service, where any concrete word belongs to one
            confession. The label already says the field is optional. */}
        <input
          value={plan.title}
          maxLength={200}
          onChange={(event) => setPlan({ ...plan, title: event.target.value })}
        />
      </label>
    </Sheet>
  );
}
